<template>
    <div class="rooms">
        <div data-name="search-room">
            <Icon icon="chat-search-icon"/>
            <input 
                type="text" 
                placeholder="Search Messages"
                id="search-chatroom"
                v-model="query">
        </div>
        <div data-name="rooms-list">
            <ul>
                <li
                    v-for="(room, index) in filteredRooms"
                    :id="room.id"
                    :key="`room-${room.id}`"
                    :class="{
                        'active-room': $route.query.roomId === room.id,
                        'unseen-room': room.lastMessage && !room.lastMessage.seen && room.lastMessage.uid !== user.userId
                    }">
                        <router-link
                            :to="`/mymessages?roomId=${room.id}`" 
                            @click.native="trackEvent" 
                            :id="`conversation-${index}`">
                            <div data-name="room-item">
                                <div class="room-item-pic">
                                    <img v-lazy="room.myPic" alt="">
                                    <img v-lazy="room.pic" alt="">
                                </div>
                                <div class="room-item-details">
                                    <div :data-bold="unreadMessge" data-name="room-texts">
                                        <p>{{room.name }}</p>
                                        <p :class="{'p1b unseen-message': !room.lastMessage.seen && room.lastMessage.uid !== user.userId}">
                                            {{ room.lastMessage.message }}
                                        </p>
                                    </div>
                                    <div data-name="details-footer">
                                        <RoomRequest 
                                            v-if="room.requests && room.requests.length"
                                            :requests="room.requests" 
                                            :room-id="room.id"
                                        />
                                        <p v-else></p>   
                                        <p class="date" v-if="room.lastMessageDate">
                                            {{room.lastMessageDate | toChatDateFormat}}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </router-link>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { computed, ref, watchEffect, reactive, onMounted, watch } from '@vue/composition-api'
import { SearchIcon } from '../../components/Icons'
import { useStore } from '../../core/hooks'
import Icon from '../../components/Theme/Icon'
import RoomRequest from './RoomRequest'
import {dates} from '../../core/filters'

export default {
    name: 'chat-rooms',
    components: {
        SearchIcon,
        Icon,
        RoomRequest
    },
    props: [
        'rooms',
        'room',
    ],
    data() {
        return {
            activeRoom: {
                background: '#f4f4f4'
            }
        }
    },
    filters: {
        toChatDateFormat: dates.toChatDateFormat
    },
    setup: function (props, context) {
        const { state } = useStore()
        const query = ref('')
        const filteredRooms = computed(
            () => {
                if (query.value.length) {
                    return props.rooms.filter(
                        (room) => room.name.toLowerCase().indexOf(query.value) > -1 && room.lastMessage.message
                    )
                }

                return props.rooms.filter(room => room.lastMessage.message)
            }
        )

        const user = state.value.user

        const unreadMessge = computed(() => {
            return props.room && props.room.lastMessage && !props.room.lastMessage.seen && props.room.lastMessage.uid !== user.uid
        })
        const trackEvent = () => {
            context.root.$sendMixpanelEvent('Chatroom Conversation Selected')
            context.root.$ga.event('Chat', 'Chatroom Conversation Selected')
        }

        return {
            filteredRooms,
            query,
            user,
            trackEvent,
            unreadMessge
        }
    },
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/mixins';
div.rooms {
    max-width: 440px;
    height: 100%;
    border-right: 1px solid #D6DDE3;
    display: flex;
    flex-direction: column;

    div[data-name="search-room"] {
        height: 88px;
        max-height: 88px;
        min-height: 88px;
        display: flex;
        align-items: center;
        border-bottom: 1px solid #D6DDE3;
        padding-left: 32px;
        input {
            font-size: 16px;
            line-height: 24px;
            color: #8DA0A9;
            margin-left: 24px;
            border: none;
        }
    }
    div[data-name="rooms-list"] {
        flex-grow: 1;
        overflow-y: auto;

        ul {
            margin: 0;
            padding: 0;
            li {
                max-height: 113px;
                padding-left: 16px;
                padding-top: 16px;
                padding-bottom: 16px;
                padding-right: 24px;
                border-bottom: 1px solid #D6DDE3;
                transition: all 0.2s ease;
                &:hover {
                    background: #F7F7FE;
                }

                &.active-room {
                    background: #e8e8fc
                }

                &.unseen-room {
                    background: #F7F7FE;
                }
                
                div[data-name="room-item"]{
                    display: flex;
                    div.room-item-pic {
                        position: relative;
                        img {
                            border-radius: 50%;
                            border: 1px solid #D6DDE3;
                            object-fit: cover;
                            background-color: #fff;
                            &:first-child {
                                max-width: 52px;
                                width: 52px;
                                height: 52px;
                            }
                            &:last-child {
                                width: 44px;
                                height: 44px;
                                position: absolute;
                                top: 14px;
                                left: 14px;
                            }
                        }
                    }
                    div.room-item-details {
                        padding-left: 20px;
                        width: 100%;
                        div[data-name="room-texts"] {
                            &[data-bold="true"] {
                                p {
                                    font-weight: bold;
                                }
                            }
                            p {
                                margin-bottom: 0;
                                word-break: break-all;
                                &:first-child {
                                    color: #333333;
                                    font-size: 16px; 
                                    font-weight: bold;
                                    overflow: hidden;
                                    display: -webkit-box;
                                    -webkit-box-orient: vertical;
                                    -webkit-line-clamp: 1;
                                }
                                &:last-child {
                                    color: #8D9FA8;
                                    font-size: 13px;
                                    overflow: hidden;
                                    display: -webkit-box;
                                    -webkit-box-orient: vertical;
                                    -webkit-line-clamp: 1;

                                    &.unseen-message {
                                        color: #374349
                                    }
                                }
                            }
                        }
                        div[data-name="details-footer"] {
                            margin-top: 16px;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            .date {
                                margin-bottom: 0;
                                font-size: 11px;
                                color: #8DA0A9;
                            }
                        }
                    }
                }
            }
        }
    }
}

@media (min-width: 992px) { 
    div.rooms {
        min-width: 440px;
    }
}
@media (max-width: 992px) {
    div.rooms {
        div[data-name="search-room"] {
            display: none;
        }
        div[data-name="rooms-list"] {
            li {
                min-height: 88px;
            }
            div.room-item-details {
                display: none;
            }
        }
    }
}
</style>